import type { ReactNode } from "react";
import { i18n, type Messages } from "@lingui/core";
import { I18nProvider } from "@lingui/react";
import { currentLocale, intlLocale } from "@ghezaa/ui";

/** The app's own catalogue for a language: `(locale) => import(`./locales/${locale}.po`)`, compiled by Lingui's Vite plugin. */
export type CatalogLoader = (locale: string) => Promise<{ messages: Messages }>;

/**
 * Loads the shared catalogue (@ghezaa/ui and @ghezaa/shell) and the app's, for the language `initLocale()` settled,
 * and activates them. Call once, before the first render.
 */
export async function loadCatalogs(loadApp: CatalogLoader): Promise<void> {
  const locale = currentLocale();
  const [shared, app] = await Promise.all([
    import(`./locales/${locale}.po`) as Promise<{ messages: Messages }>,
    loadApp(locale),
  ]);
  i18n.loadAndActivate({ locale, locales: intlLocale(), messages: { ...shared.messages, ...app.messages } });
}

/** Around the whole app, so every `<Trans>` and `useLingui()` finds the active catalogue. */
export function LocaleProvider({ children }: { children: ReactNode }) {
  return <I18nProvider i18n={i18n}>{children}</I18nProvider>;
}
